import { logger } from "@/lib/logger";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, PieChart, BarChart3, RefreshCw, Download } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";

interface DiversityCategory {
  category: string;
  value: string;
  count: number;
  percentage: number;
}

export default function SiteDiversityDashboard() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();

  // Fetch diversity data for this site
  const { data: diversityData, isLoading, refetch } = useQuery({
    queryKey: ['site-diversity-details', siteId],
    queryFn: async () => {
      // Get all patients assigned to this site
      const { data: assignments, error: assignError } = await supabase
        .from('patient_trial_site_assignments')
        .select('profile_id')
        .eq('trial_site_id', siteId!);

      if (assignError) {
        logger.error('Error fetching assignments:', assignError);
        return { race: [], ethnicity: [], totalPatients: 0, patientsWithData: 0 };
      }

      const profileIds = assignments?.map(a => a.profile_id) || [];
      const totalPatients = profileIds.length;
      
      if (profileIds.length === 0) {
        return { race: [], ethnicity: [], totalPatients: 0, patientsWithData: 0 };
      }

      // Get diversity data for these patients
      const { data: diversityRecords, error: diversityError } = await supabase
        .from('diversity_analytics')
        .select('demographic_category, demographic_value, profile_id')
        .in('profile_id', profileIds)
        .in('demographic_category', ['race', 'ethnicity']);

      if (diversityError) {
        logger.warn('Error fetching diversity data:', diversityError);
        return { race: [], ethnicity: [], totalPatients, patientsWithData: 0 };
      }

      // Aggregate by category
      const raceMap = new Map<string, number>();
      const ethnicityMap = new Map<string, number>();
      const profilesWithData = new Set<string>();

      diversityRecords?.forEach(record => {
        if (record.demographic_value) {
          profilesWithData.add(record.profile_id);
          if (record.demographic_category === 'race') {
            raceMap.set(record.demographic_value, (raceMap.get(record.demographic_value) || 0) + 1);
          } else if (record.demographic_category === 'ethnicity') {
            ethnicityMap.set(record.demographic_value, (ethnicityMap.get(record.demographic_value) || 0) + 1);
          }
        }
      });

      const mapToArray = (map: Map<string, number>, category: string): DiversityCategory[] => {
        const total = Array.from(map.values()).reduce((sum, count) => sum + count, 0);
        return Array.from(map.entries())
          .map(([value, count]) => ({
            category,
            value,
            count,
            percentage: total > 0 ? (count / total) * 100 : 0,
          }))
          .sort((a, b) => b.count - a.count);
      };

      return {
        race: mapToArray(raceMap, 'race'),
        ethnicity: mapToArray(ethnicityMap, 'ethnicity'),
        totalPatients,
        patientsWithData: profilesWithData.size,
      };
    },
    enabled: !!siteId,
  });

  if (!siteId) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <p className="text-center">Site ID is required</p>
        </div>
      </Layout>
    );
  }

  const completionRate = diversityData?.totalPatients 
    ? ((diversityData.patientsWithData / diversityData.totalPatients) * 100).toFixed(1)
    : '0.0';

  return (
    <Layout>
      <SEOHead
        title="Diversity Demographics Dashboard"
        description="View diversity and demographic data for site patients"
        url={`/trial-site/${siteId}/diversity`}
      />

      <div className="container mx-auto px-4 py-8 pb-24 md:pb-8">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(`/trial-site/${siteId}/patient-management`)} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Patient Management
          </Button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-purple-500" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Diversity Demographics</h1>
                <p className="text-muted-foreground">
                  Demographic breakdown for your patient cohort
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Export Report
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Patients</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <p className="text-3xl font-bold">{diversityData?.totalPatients || 0}</p>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">With Diversity Data</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <p className="text-3xl font-bold text-purple-600">{diversityData?.patientsWithData || 0}</p>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Data Completion</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-full" />
              ) : (
                <div className="space-y-2">
                  <p className="text-3xl font-bold">{completionRate}%</p>
                  <Progress value={parseFloat(completionRate)} className="h-2" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Diversity Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Race */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <PieChart className="h-5 w-5 text-purple-500" />
                <CardTitle>Race Distribution</CardTitle>
              </div>
              <CardDescription>
                Self-reported race categories
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              ) : diversityData?.race && diversityData.race.length > 0 ? (
                <div className="space-y-3">
                  {diversityData.race.map((item) => (
                    <div key={item.value} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{item.value}</span>
                        <span className="text-muted-foreground">
                          {item.count} ({item.percentage.toFixed(1)}%)
                        </span>
                      </div>
                      <Progress value={item.percentage} className="h-2" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No race data collected yet
                </p>
              )}
            </CardContent>
          </Card>

          {/* Ethnicity */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-purple-500" />
                <CardTitle>Ethnicity Distribution</CardTitle>
              </div>
              <CardDescription>
                Self-reported ethnicity categories
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              ) : diversityData?.ethnicity && diversityData.ethnicity.length > 0 ? (
                <div className="space-y-3">
                  {diversityData.ethnicity.map((item) => (
                    <div key={item.value} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{item.value}</span>
                        <span className="text-muted-foreground">
                          {item.count} ({item.percentage.toFixed(1)}%)
                        </span>
                      </div>
                      <Progress value={item.percentage} className="h-2" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No ethnicity data collected yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
